import { useEffect } from 'react';

export const useFullPageScroll = () => {
  useEffect(() => {
    let isScrolling = false;
    let currentSection = 0;
    let lastScrollTime = 0;
    
    // Detect which sections are present in the DOM in the correct order
    const allPossibleSections = ['hero', 'technology', 'use-cases', 'dashboard', 'why-now', 'business-model', 'why-us', 'features', 'projections', 'seed-round', 'team', 'timeline', 'about', 'beta', 'articles', 'contact'];
    const sections: string[] = [];
    
    allPossibleSections.forEach(sectionId => {
      const element = document.getElementById(sectionId) || (sectionId === 'hero' ? document.querySelector('.hero-section') : null);
      if (element) {
        sections.push(sectionId);
      }
    });
    
    const updateActiveNav = (sectionIndex: number) => {
      const dots = document.querySelectorAll('.nav-dot');
      const sectionNav = document.querySelector('.section-nav');
      
      // Determine if we're on a dark or light section
      const darkSectionIds = ['hero', 'features', 'contact', 'seed-round', 'why-now', 'beta'];
      const currentSectionId = sections[sectionIndex];
      const isOnDarkSection = darkSectionIds.includes(currentSectionId);
      
      if (sectionNav) {
        if (isOnDarkSection) {
          sectionNav.classList.remove('light-bg');
          sectionNav.classList.add('dark-bg');
        } else {
          sectionNav.classList.remove('dark-bg');
          sectionNav.classList.add('light-bg');
        }
      }
      
      dots.forEach((dot, index) => {
        if (index === sectionIndex) {
          dot.classList.add('active');
        } else {
          dot.classList.remove('active');
        }
      });
    };
    
    const scrollToSection = (sectionIndex: number) => {
      if (sectionIndex < 0 || sectionIndex >= sections.length) return;
      
      isScrolling = true;
      currentSection = sectionIndex;
      updateActiveNav(sectionIndex);
      
      let targetElement: HTMLElement | null;
      
      if (sectionIndex === 0) {
        targetElement = document.querySelector('.hero-section');
      } else {
        targetElement = document.getElementById(sections[sectionIndex]);
      }
      
      if (targetElement) {
        const offsetTop = sectionIndex === 0 ? 0 : targetElement.offsetTop;
        
        window.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        });
        
        setTimeout(() => {
          isScrolling = false;
          // Update nav after scroll completes
          detectCurrentSection();
        }, 1500);
      }
    };
    
    // Handle wheel scrolling
    const handleWheel = (e: WheelEvent) => {
      // Disable full-page scroll on mobile
      if (window.innerWidth <= 949) {
        return;
      }
      
      // Allow normal scrolling in beta section, timeline section, and articles section
      const currentSectionId = sections[currentSection];
      if (currentSectionId === 'beta' || currentSectionId === 'timeline' || currentSectionId === 'articles') {
        return;
      }
      
      if (isScrolling) {
        e.preventDefault();
        return;
      }
      
      // Throttle scroll events - require at least 50ms between scrolls
      const currentTime = Date.now();
      if (currentTime - lastScrollTime < 50) {
        e.preventDefault();
        return;
      }
      lastScrollTime = currentTime;
      
      e.preventDefault();
      
      if (e.deltaY > 0) {
        // Scrolling down
        if (currentSection < sections.length - 1) {
          scrollToSection(currentSection + 1);
        }
      } else {
        // Scrolling up
        if (currentSection > 0) {
          scrollToSection(currentSection - 1);
        }
      }
    };
    
    // Handle nav dot clicks
    const handleNavClick = (e: Event) => {
      const dot = e.currentTarget as HTMLElement;
      const section = dot.getAttribute('data-section');
      const index = sections.indexOf(section || '');
      
      if (index !== -1) {
        // On mobile, use standard scrolling
        if (window.innerWidth <= 949) {
          currentSection = index;
          updateActiveNav(index);
          
          let targetElement: HTMLElement | null;
          if (index === 0) {
            targetElement = document.querySelector('.hero-section');
          } else {
            targetElement = document.getElementById(sections[index]);
          }
          
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth' });
          }
        } else {
          scrollToSection(index);
        }
      }
    };
    
    // Handle keyboard navigation
    const handleKeydown = (e: KeyboardEvent) => {
      // Disable keyboard navigation on mobile
      if (window.innerWidth <= 949) {
        return;
      }
      
      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        if (currentSection < sections.length - 1) {
          scrollToSection(currentSection + 1);
        }
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        if (currentSection > 0) {
          scrollToSection(currentSection - 1);
        }
      }
    };
    
    // Detect current section on load
    const detectCurrentSection = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const threshold = windowHeight * 0.3; // Section is active when 30% visible
      
      sections.forEach((section, index) => {
        let element: HTMLElement | null;
        
        if (index === 0) {
          element = document.querySelector('.hero-section');
        } else {
          element = document.getElementById(section);
        }
        
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementTop = rect.top;
          const elementBottom = rect.bottom;
          
          // Check if element is at least 30% visible
          if (elementTop < threshold && elementBottom > threshold) {
            if (currentSection !== index) {
              currentSection = index;
              updateActiveNav(index);
            }
          }
        }
      });
    };
    
    // Handle scroll on mobile and desktop
    const handleScroll = () => {
      if (!isScrolling) {
        detectCurrentSection();
      }
    };
    
    // Add event listeners
    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('keydown', handleKeydown);
    window.addEventListener('scroll', handleScroll);
    
    const dots = document.querySelectorAll('.nav-dot');
    dots.forEach(dot => {
      dot.addEventListener('click', handleNavClick);
    });
    
    // Initial detection
    detectCurrentSection();
    
    // Cleanup
    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('keydown', handleKeydown);
      window.removeEventListener('scroll', handleScroll);
      dots.forEach(dot => {
        dot.removeEventListener('click', handleNavClick);
      });
    };
  }, []);
};